import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Brain, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { getSIAdminCoachDetail, type SICoachRow, type SIAdminClientRow, type SIAdminSessionRow } from '../../lib/si-admin';

function AnalysisStatusBadge({ status }: { status: string }) {
  if (status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-10 text-success-50">
        <CheckCircle className="w-3 h-3" /> Complete
      </span>
    );
  }
  if (status === 'processing') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
        <Clock className="w-3 h-3" /> Processing
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
        <AlertCircle className="w-3 h-3" /> Error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-10 text-gray-50">
      Idle
    </span>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-20 p-5">
      <p className="text-2xl font-bold text-gray-80">{value}</p>
      <p className="text-sm font-medium text-gray-60 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-40 mt-1">{sub}</p>}
    </div>
  );
}

export function SIAdminCoachDetail() {
  const { coachId } = useParams<{ coachId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [coach, setCoach] = useState<SICoachRow | null>(null);
  const [clients, setClients] = useState<SIAdminClientRow[]>([]);
  const [sessions, setSessions] = useState<SIAdminSessionRow[]>([]);
  const [error, setError] = useState('');
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) { navigate('/si/login', { replace: true }); return; }
    isUserAdmin(user.id).then(admin => {
      if (!admin) { navigate('/si/dashboard', { replace: true }); return; }
      setChecking(false);
      if (!coachId) return;
      getSIAdminCoachDetail(coachId)
        .then(result => {
          if (!result) { setError('Coach not found'); return; }
          setCoach(result.coach);
          setClients(result.clients);
          setSessions(result.sessions);
        })
        .catch(err => setError(err.message || 'Failed to load coach data'));
    });
  }, [user, navigate, coachId]);

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-5 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const toggleClient = (id: string) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clientSessionMap = sessions.reduce<Record<string, SIAdminSessionRow[]>>((acc, s) => {
    if (!acc[s.client_id]) acc[s.client_id] = [];
    acc[s.client_id].push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-brand-5">
      <div className="section-container py-8 space-y-8">
        {/* Back */}
        <Link
          to="/si/admin"
          className="inline-flex items-center gap-2 text-sm text-gray-50 hover:text-gray-80 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admin Dashboard
        </Link>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 p-4 rounded-2xl text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {coach && (
          <>
            {/* Coach profile */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-gray-20 p-6 flex items-start gap-5"
            >
              <div className="w-12 h-12 rounded-full bg-brand-10 flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-success-50" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-80">{coach.full_name}</h1>
                <p className="text-sm text-gray-50">{coach.email}</p>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className="text-xs text-gray-40">
                    Joined {new Date(coach.joined_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-brand-10 text-gray-60 font-medium">
                    {coach.tier.replace('_', ' ')}
                  </span>
                  {!coach.is_active && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-10 text-gray-50 font-medium">Inactive</span>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard label="Clients" value={coach.client_count} />
              <StatCard label="Sessions" value={coach.session_count} />
              <StatCard
                label="AI Analyses"
                value={coach.analyses_completed}
                sub={coach.session_count > 0 ? `${Math.round((coach.analyses_completed / coach.session_count) * 100)}% of sessions` : undefined}
              />
              <StatCard
                label="Last Session"
                value={coach.last_session_date
                  ? new Date(coach.last_session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                  : '—'}
              />
            </div>

            {/* Clients with inline sessions */}
            <div>
              <h2 className="text-base font-semibold text-gray-80 mb-3">Clients</h2>
              {clients.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-20 p-8 text-center text-sm text-gray-50">
                  No clients added yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {clients.map((client, i) => {
                    const isExpanded = expandedClients.has(client.id);
                    const clientSessions = clientSessionMap[client.id] || [];
                    return (
                      <motion.div
                        key={client.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className="bg-white rounded-2xl border border-gray-20 overflow-hidden"
                      >
                        <div className="p-4 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-brand-5 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-gray-50" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-80 truncate">{client.full_name}</p>
                              {client.email && <p className="text-xs text-gray-40 truncate">{client.email}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <div className="hidden sm:flex items-center gap-4 text-xs text-gray-50">
                              <span><span className="font-semibold text-gray-80">{client.session_count}</span> sessions</span>
                              <span><span className={`font-semibold ${client.analyses_completed > 0 ? 'text-success-50' : 'text-gray-80'}`}>{client.analyses_completed}</span> analyses</span>
                              {client.last_session_date && (
                                <span>Last: {new Date(client.last_session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                              )}
                            </div>
                            {clientSessions.length > 0 && (
                              <button
                                onClick={() => toggleClient(client.id)}
                                className="flex items-center gap-1 text-xs text-gray-50 hover:text-gray-80 transition-colors"
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            )}
                          </div>
                        </div>
                        {isExpanded && clientSessions.length > 0 && (
                          <div className="border-t border-gray-10 divide-y divide-gray-10">
                            {clientSessions.map(session => (
                              <div key={session.id} className="px-4 py-3 flex items-center justify-between gap-4 text-sm bg-brand-5">
                                <div className="flex items-center gap-3">
                                  <Brain className="w-4 h-4 text-gray-40 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs font-medium text-gray-80">
                                      {new Date(session.session_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                    <p className="text-xs text-gray-40 mt-0.5 capitalize">{session.session_type} · {session.duration_minutes}min</p>
                                  </div>
                                </div>
                                <AnalysisStatusBadge status={session.analysis_status} />
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* All sessions log */}
            <div>
              <h2 className="text-base font-semibold text-gray-80 mb-3">Session Log</h2>
              {sessions.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-20 p-8 text-center text-sm text-gray-50">
                  No sessions recorded yet.
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-brand-5 text-left">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Date</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Client</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Type</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide text-right">Duration</th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-50 uppercase tracking-wide">Analysis</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-10">
                        {sessions.map(session => (
                          <tr key={session.id} className="hover:bg-brand-5 transition-colors">
                            <td className="px-4 py-3 text-xs text-gray-80">
                              {new Date(session.session_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-80">{session.client_name}</td>
                            <td className="px-4 py-3 text-xs text-gray-50 capitalize">{session.session_type}</td>
                            <td className="px-4 py-3 text-xs text-gray-50 text-right">{session.duration_minutes}min</td>
                            <td className="px-4 py-3"><AnalysisStatusBadge status={session.analysis_status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
